/**
 * Views migrated from designv2/src/views_list.jsx (Nav + ProjectsView + UsersView).
 *
 * Nav, ProjectsView, and UsersView are verbatim ports.
 */
import {
  type CSSProperties,
  type FC,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ActualsByProject, CostRate, CostsByPerson, Person, Project } from "@shared/types";
import { TagEditor } from "../cells";
import {
  allocToHours,
  downloadCsv as downloadCsvData,
  getPerson,
  getProjectRates,
  rateForMemberAt,
  resolveCostRate,
} from "../data";
import { extrasForYmKey } from "../lib/holiday-cache";
import type { SpaMode } from "../lib/me-api";
import { sortByName } from "../lib/sort";
import { usePersistedState } from "../lib/viewState";
import { useC } from "../theme";
import { CURRENT_MONTH_KEY } from "../tokens";
import {
  Av,
  Badge,
  Btn,
  Card,
  Empty,
  InfoIcon,
  PeriodPicker,
  RoleSwitcher,
  SBadge,
  Seg,
  SortableHeader,
  useTableSort,
  useTableStyles,
} from "../ui";

/**
 * Tab visibility per role (product owner 2026-04):
 *   · Admin      — everything.
 *   · Lead       — planning flow only (no setup).
 *   · PM         — planning flow only (no Project / People setup).
 *   · Accountant — Budget + Reports only.
 *
 * Tab order: planning (Dashboard → Projects view → Person view → Bench)
 * → finance (Budget → Reports) → setup (Project setup → People setup → Admin).
 */
const ALL_TABS: Record<string, string> = {
  dashboard: "Dashboard",
  project_detail: "Projects view",
  person_detail: "Person view",
  bench: "Bench",
  budget: "Budget",
  reports: "Reports",
  projects: "Project setup",
  users: "People setup",
  admin: "Admin",
};
const PLANNING = ["dashboard", "project_detail", "person_detail", "bench", "budget", "reports"];
const SETUP = ["projects", "users", "admin"];
const ROLE_TABS: Record<string, string[]> = {
  admin: [...PLANNING, ...SETUP],
  lead: PLANNING,
  pm: PLANNING,
  accountant: ["budget", "reports"],
};
const TAB_PATHS: Record<string, string> = {
  dashboard: "/",
  project_detail: "/projects-view",
  person_detail: "/person-view",
  bench: "/bench",
  budget: "/budget",
  reports: "/reports",
  projects: "/project-setup",
  users: "/people-setup",
  admin: "/admin",
};

function shouldHandleInApp(e: MouseEvent<HTMLAnchorElement>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * People-list cost cell. Shows the default rate (resolved at the current month
 * against the person's flat `cost_rates` chain) and an InfoIcon iff one or
 * more per-project rates DIFFER from the default at the current month. The
 * hover lists the active default plus those overrides line by line, sorted
 * alphabetically by project name with Estonian collation.
 */
const PersonCostCell: FC<{
  chain: readonly CostRate[];
  projectsById: ReadonlyMap<number, Project>;
  TD: CSSProperties;
}> = ({ chain, projectsById, TD }) => {
  const { C } = useC();
  const defaultRate = resolveCostRate(chain, null, CURRENT_MONTH_KEY);
  const projectIds = Array.from(
    new Set(chain.filter((r) => r.projectId !== null).map((r) => r.projectId as number)),
  );
  const perProject = sortByName(
    projectIds
      .map((pid) => {
        const rate = resolveCostRate(chain, pid, CURRENT_MONTH_KEY);
        const name = projectsById.get(pid)?.name ?? `Project #${pid}`;
        return { pid, name, rate };
      })
      .filter((row) => row.rate !== defaultRate),
  );
  return (
    <td
      style={{
        ...TD,
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
        color: C.navyDeep,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        €{defaultRate}
        {perProject.length > 0 && (
          <InfoIcon
            variant="compact"
            ariaLabel="Active cost rates"
            tooltip={
              <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                <div>Default: €{defaultRate}/h</div>
                {perProject.map((row) => (
                  <div key={row.pid}>
                    {row.name}: €{row.rate}/h
                  </div>
                ))}
              </div>
            }
          />
        )}
      </span>
    </td>
  );
};

export interface NavProps {
  view: string;
  setView: (v: string) => void;
  user: Person;
  role: string;
  setRole: (r: string) => void;
  people: Person[];
  rangeStart: string;
  rangeEnd: string;
  setRange: (s: string, e: string) => void;
  mode: SpaMode;
  setMode: (m: SpaMode) => void;
  onLogout?: () => void;
  /** Forwarded to RoleSwitcher — only admins can flip the demo role. */
  canSwitchRole?: boolean;
  /** Optional slot inside the user-dropdown — used to host DebugMenu. */
  userDropdownExtra?: import("react").ReactNode;
}

export const Nav: FC<NavProps> = ({
  view,
  setView,
  user,
  role,
  setRole,
  people,
  rangeStart,
  rangeEnd,
  setRange,
  mode,
  setMode,
  onLogout,
  canSwitchRole,
  userDropdownExtra,
}) => {
  const { C } = useC();
  const tabIds = ROLE_TABS[role] ?? ROLE_TABS["admin"] ?? [];
  // Every ROLE_TABS entry above starts with at least one tab; the empty-array
  // branch only fires if `admin` is somehow missing. Default to `dashboard`.
  const homeTab = tabIds[0] ?? "dashboard";

  // Hamburger fallback — if the tabs row is narrower than its scrollWidth
  // (would otherwise wrap or cut off), show a menu button.
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    function check() {
      const el = tabsRef.current;
      if (!el) return;
      setCollapsed(el.scrollWidth > el.clientWidth + 1);
    }
    check();
    const ro = new ResizeObserver(check);
    if (tabsRef.current) ro.observe(tabsRef.current);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, []);

  const tabBtn = (id: string) => {
    const label = ALL_TABS[id];
    const active = view === id;
    return (
      <a
        key={id}
        href={TAB_PATHS[id] ?? "/"}
        onClick={(e) => {
          if (!shouldHandleInApp(e)) return;
          e.preventDefault();
          setView(id);
        }}
        style={{
          padding: "6px 12px",
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
          fontSize: 12.5,
          background: active ? C.blueTint2 : "transparent",
          color: active ? C.blue : C.slate,
          whiteSpace: "nowrap",
          transition: "all .12s",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          textDecoration: "none",
        }}
      >
        {/* Stacked-grid trick: a hidden bold sibling reserves the width so
            the layout doesn't nudge when the active state flips weight. */}
        <span style={{ display: "inline-grid", gridTemplate: "1fr / 1fr" }}>
          <span
            aria-hidden="true"
            style={{
              gridArea: "1 / 1",
              visibility: "hidden",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          <span
            style={{
              gridArea: "1 / 1",
              fontWeight: active ? 600 : 500,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        </span>
      </a>
    );
  };

  return (
    <header
      style={{
        background: C.surface,
        borderBottom: `1px solid ${C.line}`,
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Row 1: Brand + Role */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 18px 6px",
        }}
      >
        <button
          type="button"
          onClick={() => setView(homeTab)}
          title="Home"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "transparent",
            border: "none",
            padding: "4px 6px",
            margin: "-4px -6px",
            borderRadius: 6,
            cursor: "pointer",
            transition: "background .12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = C.canvas;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          {/* SVG artwork extends to y≈50 — height tracks the real bounds via
              the 44×50 aspect so the bottom chevron isn't clipped. Same fix
              as components/BrandHeader.tsx. */}
          <svg
            width="26"
            height={26 * (50 / 44)}
            viewBox="0 0 44 50"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M43.154 15.38L6.585 36.55l.699.4h.05l2.544 1.498 33.276-19.274v3.795L13.221 40.396l.698.4 2.594 1.497 26.641-15.529v3.795L19.806 44.241l.748.4 2.545 1.497 20.055-11.784v3.795l-16.713 9.936-3.292 1.898-3.293-1.947-2.594-1.498-.749-.4-3.292-1.947-2.595-1.498-.698-.4-3.293-1.947-3.342-1.898L0 36.501l3.293-1.897 39.861-23.02z"
              fill={C.blue}
            />
            <path
              d="M43.154 34.354L6.685 13.282l.699-.4 2.594-1.497 33.176 19.174v-3.795L13.271 9.487l.648-.4h.05l2.544-1.497 26.641 15.38v-3.796L19.806 5.692l.748-.4L23.1 3.796l20.055 11.584v-3.795L26.391 1.897 23.1 0l-3.293 1.897-2.544 1.498-.749.4-3.242 1.897-2.595 1.498-.698.4-3.293 1.897-3.293 1.898L.1 13.282l3.292 1.898 39.762 22.969z"
              fill={C.blue}
            />
          </svg>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: C.navyDeep,
                letterSpacing: -0.2,
                fontFamily: "'Poppins',system-ui,sans-serif",
                lineHeight: 1,
              }}
            >
              DOLM IT
            </div>
            <div
              style={{
                fontSize: 9,
                color: C.slateMut,
                fontWeight: 500,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                marginTop: 2,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Resource Planner
            </div>
          </div>
        </button>
        <RoleSwitcher
          role={role}
          setRole={setRole}
          people={people}
          onLogout={onLogout}
          canSwitchRole={canSwitchRole}
          me={user}
          extraSlot={userDropdownExtra}
        />
      </div>

      {/* Row 2: Tabs (or hamburger when narrow) */}
      <div
        style={{
          padding: "0 18px",
          borderTop: `1px solid ${C.lineSoft}`,
          position: "relative",
        }}
      >
        <div
          ref={tabsRef}
          aria-hidden={collapsed}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            overflow: "hidden",
            height: 38,
            visibility: collapsed ? "hidden" : "visible",
            pointerEvents: collapsed ? "none" : "auto",
          }}
        >
          {tabIds.map(tabBtn)}
        </div>
        {collapsed && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 18,
              right: 18,
              height: 38,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: C.surface,
            }}
          >
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={menuOpen}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${C.line}`,
                background: menuOpen ? C.blueTint1 : C.surface,
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 600,
                color: C.navy,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M2 4h10M2 7h10M2 10h10"
                  stroke={C.slate}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              {ALL_TABS[view] || "Menu"}
            </button>
            {menuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 60,
                    background: "transparent",
                    border: 0,
                    cursor: "default",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 18,
                    marginTop: 2,
                    background: C.surface,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    boxShadow: C.shL,
                    minWidth: 200,
                    padding: 4,
                    zIndex: 61,
                  }}
                >
                  {tabIds.map((id) => (
                    <a
                      key={id}
                      href={TAB_PATHS[id] ?? "/"}
                      onClick={(e) => {
                        if (!shouldHandleInApp(e)) return;
                        e.preventDefault();
                        setView(id);
                        setMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "7px 12px",
                        borderRadius: 5,
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12.5,
                        fontWeight: view === id ? 600 : 500,
                        background: view === id ? C.blueTint1 : "transparent",
                        color: view === id ? C.blue : C.navy,
                        fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                        textDecoration: "none",
                      }}
                    >
                      {ALL_TABS[id]}
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Row 3: Mode toggle + Period picker */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "8px 18px 10px",
          borderTop: `1px solid ${C.lineSoft}`,
          flexWrap: "wrap",
          rowGap: 8,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            background: C.canvas,
            borderRadius: 7,
            padding: 2,
            gap: 1,
            border: `1px solid ${C.line}`,
            height: 30,
            boxSizing: "border-box",
          }}
        >
          {(
            [
              ["planned", "Planned"],
              ["actuals", "Actuals"],
            ] as const
          ).map(([v, l]) => (
            <button
              type="button"
              key={v}
              onClick={() => setMode(v)}
              title={v === "actuals" ? "Logged / realised hours" : "Planned allocations"}
              style={{
                padding: "0 12px",
                borderRadius: 5,
                border: "none",
                fontSize: 11.5,
                fontWeight: mode === v ? 600 : 500,
                background: mode === v ? C.surface : "transparent",
                color: mode === v ? (v === "actuals" ? C.navy : C.blue) : C.slateMut,
                boxShadow: mode === v ? C.sh : "none",
                cursor: "pointer",
                transition: "all .12s",
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                letterSpacing: 0.2,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: v === "actuals" ? C.navy : C.blue,
                  opacity: mode === v ? 1 : 0.4,
                }}
              />
              {l}
            </button>
          ))}
        </div>
        <PeriodPicker startKey={rangeStart} endKey={rangeEnd} onChange={setRange} />
      </div>
    </header>
  );
};

export interface ProjectsViewProps {
  projects: Project[];
  people: Person[];
  costs: CostsByPerson;
  role: string;
  currentUserId: number | null;
  mode: SpaMode;
  actuals: ActualsByProject;
  rangeStart: string;
  rangeEnd: string;
  onOpen: (project: Project) => void;
  onEdit: (project: Project) => void;
  onNew: () => void;
  openPerson?: (pid: number) => void;
}

export const ProjectsView: FC<ProjectsViewProps> = ({
  projects,
  people,
  role,
  currentUserId,
  mode,
  actuals,
  onOpen,
  onEdit,
  onNew,
  openPerson,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState<string>(
    "projects",
    "statusFilter",
    "all",
  );
  /* PM visibility rules (product owner 2026-04):
       · PM never sees Budget €, Margin Target %, or pricing-model labels —
         those leak cost/profit intel.
       · PM sees Prognosis € only on projects they own; everyone else's cell
         blanks to "—". Accountant + Admin see everything. */
  const canSeeCost = role === "admin" || role === "lead" || role === "accountant";
  const isPmView = role === "pm";

  function projectStats(p: Project) {
    const pidSet = new Set(p.members.map((m) => m.pid).filter(Boolean));
    const unassigned = p.members.filter((m) => !m.pid).length;
    let hours = 0;
    let rev = 0;
    for (const m of p.members) {
      const pers = m.pid ? getPerson(m.pid, people) : null;
      const pObj = { wl: pers?.wl || 100 };
      if (mode === "actuals" && m.pid) {
        const monthly = (actuals[p.id] && actuals[p.id]?.[m.pid]) || {};
        for (const [ym, h] of Object.entries(monthly)) {
          hours += h;
          rev += h * rateForMemberAt(p, m, ym);
        }
      } else {
        for (const [ym, a] of Object.entries(m.a)) {
          const h = allocToHours(a, pObj, ym, extrasForYmKey(ym));
          hours += h;
          rev += h * rateForMemberAt(p, m, ym);
        }
      }
    }
    return { people: pidSet.size, unassigned, hours: Math.round(hours), rev: Math.round(rev) };
  }

  type ProjectSortKey =
    | "project"
    | "status"
    | "pm"
    | "team"
    | "hours"
    | "budget"
    | "prognosis"
    | "mt";
  const projectSort = useTableSort<Project, ProjectSortKey>({
    project: (p) => p.name.toLowerCase(),
    status: (p) => p.status,
    pm: (p) => (p.pmId != null ? (getPerson(p.pmId, people)?.name ?? "").toLowerCase() : ""),
    team: (p) => projectStats(p).people,
    hours: (p) => projectStats(p).hours,
    budget: (p) => p.budget,
    prognosis: (p) => projectStats(p).rev,
    mt: (p) => p.mt,
  });

  const filtered = (() => {
    let rows = projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (
        q &&
        !(
          p.name.toLowerCase().includes(q.toLowerCase()) ||
          p.code.toLowerCase().includes(q.toLowerCase())
        )
      )
        return false;
      return true;
    });
    rows = projectSort.sort(rows);
    return rows;
  })();

  function exportCsv() {
    const headers: Array<string | number> = [
      "Code",
      "Name",
      "Status",
      "PM",
      "Team",
      "Unassigned",
      "Hours",
    ];
    if (!isPmView || canSeeCost) headers.push("Prognosis EUR");
    if (canSeeCost) headers.push("Budget EUR", "Margin Target %");
    const rows: Array<Array<string | number>> = [headers];
    for (const p of filtered) {
      const s = projectStats(p);
      const pm = p.pmId != null ? getPerson(p.pmId, people) : null;
      const row: Array<string | number> = [
        p.code,
        p.name,
        p.status,
        pm?.name || "",
        s.people,
        s.unassigned,
        s.hours,
      ];
      if (isPmView) row.push(p.pmId === currentUserId ? s.rev : "");
      else if (canSeeCost) row.push(s.rev);
      if (canSeeCost) row.push(p.budget, p.mt);
      rows.push(row);
    }
    downloadCsvData(`dolmit-projects-${mode}.csv`, rows);
  }

  /* Project create/edit is now admin-only. PMs can allocate people to existing
     projects but cannot create, rename, reprice, or rebudget them. */
  const canNew = role === "admin" || role === "lead";

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1400, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Projects
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            {projects.length} total · {projects.filter((p) => p.status === "active").length} active
            · {projects.filter((p) => p.status === "planned").length} planned · showing{" "}
            <strong style={{ color: mode === "actuals" ? C.navy : C.blue }}>
              {mode === "actuals" ? "actuals" : "planned"}
            </strong>{" "}
            totals
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <circle cx="7" cy="7" r="5" stroke={C.slateMut} strokeWidth="1.5" fill="none" />
              <path d="M11 11l3 3" stroke={C.slateMut} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              aria-label="Search projects"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              style={{
                padding: "6px 12px 6px 30px",
                borderRadius: 6,
                border: `1px solid ${C.line}`,
                fontSize: 12.5,
                color: C.navy,
                background: C.surface,
                outline: "none",
                width: 200,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            />
          </div>
          <Seg
            options={[
              ["all", "All"],
              ["active", "Active"],
              ["planned", "Planned"],
              ["archived", "Archived"],
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <Btn
            small
            onClick={exportCsv}
            icon={
              <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            Export CSV
          </Btn>
          {canNew && (
            <Btn primary onClick={onNew}>
              + New Project
            </Btn>
          )}
        </div>
      </div>

      <Card style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <SortableHeader
                label="Project"
                align="left"
                active={projectSort.isActive("project")}
                indicator={projectSort.indicator("project")}
                ariaSort={projectSort.ariaSortFor("project")}
                onSort={() => projectSort.toggle("project")}
                TH={TH}
              />
              <SortableHeader
                label="Status"
                align="center"
                active={projectSort.isActive("status")}
                indicator={projectSort.indicator("status")}
                ariaSort={projectSort.ariaSortFor("status")}
                onSort={() => projectSort.toggle("status")}
                TH={TH}
              />
              <SortableHeader
                label="PM"
                align="left"
                active={projectSort.isActive("pm")}
                indicator={projectSort.indicator("pm")}
                ariaSort={projectSort.ariaSortFor("pm")}
                onSort={() => projectSort.toggle("pm")}
                TH={TH}
              />
              <SortableHeader
                label="Team"
                align="right"
                active={projectSort.isActive("team")}
                indicator={projectSort.indicator("team")}
                ariaSort={projectSort.ariaSortFor("team")}
                onSort={() => projectSort.toggle("team")}
                TH={TH}
              />
              <SortableHeader
                label={mode === "actuals" ? "Logged Hours" : "Planned Hours"}
                align="right"
                active={projectSort.isActive("hours")}
                indicator={projectSort.indicator("hours")}
                ariaSort={projectSort.ariaSortFor("hours")}
                onSort={() => projectSort.toggle("hours")}
                TH={TH}
              />
              {canSeeCost && (
                <SortableHeader
                  label="Budget"
                  align="right"
                  active={projectSort.isActive("budget")}
                  indicator={projectSort.indicator("budget")}
                  ariaSort={projectSort.ariaSortFor("budget")}
                  onSort={() => projectSort.toggle("budget")}
                  TH={TH}
                />
              )}
              <SortableHeader
                label="Prognosis"
                align="right"
                active={projectSort.isActive("prognosis")}
                indicator={projectSort.indicator("prognosis")}
                ariaSort={projectSort.ariaSortFor("prognosis")}
                onSort={() => projectSort.toggle("prognosis")}
                TH={TH}
              />
              {canSeeCost && (
                <SortableHeader
                  label="Margin Target"
                  align="center"
                  active={projectSort.isActive("mt")}
                  indicator={projectSort.indicator("mt")}
                  ariaSort={projectSort.ariaSortFor("mt")}
                  onSort={() => projectSort.toggle("mt")}
                  TH={TH}
                />
              )}
              <th style={{ ...TH, textAlign: "right", width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => {
              const s = projectStats(p);
              const pm = p.pmId != null ? getPerson(p.pmId, people) : null;
              const pctOfBudget = p.budget > 0 ? Math.round((s.rev / p.budget) * 100) : 0;
              return (
                <tr
                  key={p.id}
                  onClick={() => onOpen(p)}
                  style={{
                    cursor: "pointer",
                    transition: "background .1s",
                    background: i % 2 ? C.surfaceAlt : C.surface,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = C.blueTint1;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = i % 2 ? C.surfaceAlt : C.surface;
                  }}
                >
                  <td style={{ ...TD, textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: `linear-gradient(135deg,${C.blueTint3},${C.blueTint4})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: C.blue,
                          fontWeight: 700,
                          fontSize: 11,
                          fontFamily: "'Poppins',system-ui,sans-serif",
                        }}
                      >
                        {p.code.slice(0, 3)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: C.navyDeep, fontSize: 13 }}>
                          {p.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.slateMut,
                            marginTop: 1,
                            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                          }}
                        >
                          {p.code}
                          {canSeeCost &&
                            (() => {
                              /* Show the CURRENT flat rate (latest rateHistory entry ≤ now,
                                 or legacy p.fr). Append "· indexed" when there's a >1-entry
                                 history so users know the rate changes over time. */
                              if (p.pm !== "flat") return " · Role-based rates";
                              const { fr } = getProjectRates(p, CURRENT_MONTH_KEY);
                              const hist = p.rateHistory;
                              const indexed = Array.isArray(hist) && hist.length > 1;
                              return ` · Flat €${fr}/h${indexed ? " · indexed" : ""}`;
                            })()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...TD, textAlign: "center" }}>
                    <SBadge s={p.status} />
                  </td>
                  <td style={{ ...TD, textAlign: "left" }}>
                    {pm ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPerson?.(pm.id);
                        }}
                        title="Open in Person view"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <Av name={pm.name} sz={22} />
                        <span
                          style={{
                            fontSize: 12.5,
                            color: C.navy,
                            fontWeight: 600,
                            textDecoration: "underline",
                            textDecorationColor: "transparent",
                            textUnderlineOffset: 2,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecorationColor = C.blue;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecorationColor = "transparent";
                          }}
                        >
                          {pm.name}
                        </span>
                      </button>
                    ) : (
                      <span style={{ color: C.slateMut }}>—</span>
                    )}
                  </td>
                  <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: C.navy, fontWeight: 600 }}>{s.people}</span>
                    {s.unassigned > 0 && (
                      <span style={{ color: C.r, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
                        +{s.unassigned} unassigned
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      ...TD,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: C.slate,
                    }}
                  >
                    {s.hours.toLocaleString()} h
                  </td>
                  {canSeeCost && (
                    <td
                      style={{
                        ...TD,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 600,
                        color: C.navyDeep,
                      }}
                    >
                      €{p.budget.toLocaleString()}
                    </td>
                  )}
                  <td style={{ ...TD, textAlign: "right" }}>
                    {isPmView && p.pmId !== currentUserId ? (
                      <span style={{ color: C.slateDim, fontSize: 12 }}>—</span>
                    ) : (
                      <>
                        <div
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            color: C.navy,
                            fontSize: 12.5,
                          }}
                        >
                          €{s.rev.toLocaleString()}
                        </div>
                        {canSeeCost && (
                          <>
                            <div
                              style={{
                                height: 4,
                                background: C.lineSoft,
                                borderRadius: 2,
                                marginTop: 3,
                                overflow: "hidden",
                                width: 90,
                                marginLeft: "auto",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${Math.min(100, pctOfBudget)}%`,
                                  background:
                                    pctOfBudget > 100
                                      ? C.r
                                      : pctOfBudget >= 80
                                        ? C.blue
                                        : C.blueTint5,
                                  borderRadius: 2,
                                  transition: "width .3s",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.slateMut,
                                marginTop: 2,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {pctOfBudget}% of budget
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  {canSeeCost && (
                    <td
                      style={{
                        ...TD,
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 600,
                        color: C.g,
                      }}
                    >
                      {p.mt}%
                    </td>
                  )}
                  <td style={{ ...TD, textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    {role === "admin" || role === "lead" ? (
                      <Btn small onClick={() => onEdit(p)}>
                        Edit
                      </Btn>
                    ) : (
                      <span style={{ fontSize: 10.5, color: C.slateMut }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <Empty title="No projects match" msg="Try adjusting your filters or search query." />
        )}
      </Card>
    </div>
  );
};

const SYSROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  lead: "Team Lead",
  pm: "PM",
  accountant: "Accountant",
};

const SYSROLE_TONE: Record<string, "red" | "violet" | "yellow" | "blue"> = {
  admin: "red",
  lead: "violet",
  accountant: "yellow",
  pm: "blue",
};

export interface UsersViewProps {
  people: Person[];
  costs: CostsByPerson;
  /** Used by the per-project rate hover tooltip on the cost column to resolve
   *  project ids to names. Optional — the tooltip degrades to ids if absent. */
  projects?: Project[];
  role: "admin" | "lead" | "pm" | "accountant" | string;
  /** Job-role suggestions for inline TagEditor (catalogue from /api/job-roles). */
  suggestions: string[];
  /** Called when a row's inline tag editor commits a new tag list. */
  onUpdateRoles: (id: number, next: string[]) => void;
  openPerson: (pid: number) => void;
  onEdit: (p: Person) => void;
  onNew: () => void;
}

export const UsersView: FC<UsersViewProps> = ({
  people,
  costs,
  projects = [],
  role,
  suggestions,
  onUpdateRoles,
  openPerson,
  onEdit,
  onNew,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const isAdmin = role === "admin" || role === "lead";
  const canSeeCost = role === "admin" || role === "lead" || role === "accountant";
  // O(1) name lookup for the per-project rate tooltip — beats projects.find()
  // per row × override.
  const projectsById = useMemo(() => new Map(projects.map((pp) => [pp.id, pp])), [projects]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState<
    "all" | "active" | "archived" | "excluded"
  >("people", "statusFilter", "all");
  type PeopleSortKey = "name" | "email" | "status" | "roles" | "sysRole" | "emp" | "wl" | "cost";
  const { sort, toggle, isActive, indicator, ariaSortFor } = useTableSort<Person, PeopleSortKey>({
    name: (p) => p.name,
    email: (p) => p.email,
    status: (p) => p.status,
    // Job-roles column sorts by the first tag (joined-by-comma alternative
    // is misleading when comparing rows; first tag is the canonical primary).
    roles: (p) => (p.roles[0] ?? "").toLowerCase(),
    // Permission: nulls (planning-only) sort to the end on asc, top on desc.
    sysRole: (p) => p.sysRole,
    emp: (p) => p.emp,
    // Workload: stored as wl (FTE %). Numeric sort.
    wl: (p) => p.wl,
    // Cost per hour comes from the costs map keyed by person id.
    cost: (p) => resolveCostRate(costs[p.id] ?? [], null, CURRENT_MONTH_KEY) || null,
  });

  const filtered = (() => {
    let rows = people;
    if (statusFilter === "excluded") {
      rows = rows.filter((p) => p.excludeFromPlanning);
    } else if (statusFilter !== "all") {
      rows = rows.filter((p) => p.status === statusFilter);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.email.toLowerCase().includes(needle) ||
          p.roles.some((r) => r.toLowerCase().includes(needle)),
      );
    }
    rows = sort(rows);
    return rows;
  })();

  function exportCsv() {
    const rows: Array<Array<string | number>> = [
      ["Name", "Email", "System Role", "Employment", "Current Workload %", "Cost/h EUR", "Periods"],
    ];
    for (const p of people) {
      const periods = p.periods.map((pr) => `${pr.from}..${pr.to}@${pr.wl}%`).join(";");
      rows.push([
        p.name,
        p.email,
        p.sysRole ?? "(planning only)",
        p.emp,
        p.wl,
        canSeeCost ? resolveCostRate(costs[p.id] ?? [], null, CURRENT_MONTH_KEY) : "—",
        periods,
      ]);
    }
    downloadCsvData("dolmit-people.csv", rows);
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1250, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            People &amp; Workload
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            {people.length} people · {people.filter((p) => p.emp === "full").length} full-time ·{" "}
            {people.filter((p) => p.emp === "part").length} variable
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <circle cx="7" cy="7" r="5" stroke={C.slateMut} strokeWidth="1.5" fill="none" />
              <path d="M11 11l3 3" stroke={C.slateMut} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              aria-label="Search people"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              style={{
                padding: "6px 12px 6px 30px",
                borderRadius: 6,
                border: `1px solid ${C.line}`,
                fontSize: 12.5,
                color: C.navy,
                background: C.surface,
                outline: "none",
                width: 200,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            />
          </div>
          <Seg
            options={[
              ["all", "All"],
              ["active", "Active"],
              ["archived", "Archived"],
              ["excluded", "Excluded"],
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as "all" | "active" | "archived" | "excluded")}
          />
          <Btn
            small
            onClick={exportCsv}
            icon={
              <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            Export CSV
          </Btn>
          {isAdmin && (
            <Btn primary onClick={onNew}>
              + Add Person
            </Btn>
          )}
        </div>
      </div>
      {/* overflow:visible so the per-project rate InfoIcon tooltip in the cost
          column isn't clipped by the Card's rounded-corner mask. */}
      <Card style={{ overflow: "visible" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <SortableHeader
                label="Name"
                align="left"
                active={isActive("name")}
                indicator={indicator("name")}
                ariaSort={ariaSortFor("name")}
                onSort={() => toggle("name")}
                TH={TH}
              />
              <SortableHeader
                label="Email"
                align="left"
                active={isActive("email")}
                indicator={indicator("email")}
                ariaSort={ariaSortFor("email")}
                onSort={() => toggle("email")}
                TH={TH}
              />
              <SortableHeader
                label="Status"
                align="center"
                active={isActive("status")}
                indicator={indicator("status")}
                ariaSort={ariaSortFor("status")}
                onSort={() => toggle("status")}
                TH={TH}
              />
              <SortableHeader
                label="Job Roles"
                align="left"
                minWidth={220}
                active={isActive("roles")}
                indicator={indicator("roles")}
                ariaSort={ariaSortFor("roles")}
                onSort={() => toggle("roles")}
                TH={TH}
              />
              <SortableHeader
                label="Permission"
                align="center"
                active={isActive("sysRole")}
                indicator={indicator("sysRole")}
                ariaSort={ariaSortFor("sysRole")}
                onSort={() => toggle("sysRole")}
                TH={TH}
              />
              <SortableHeader
                label="Employment"
                align="center"
                active={isActive("emp")}
                indicator={indicator("emp")}
                ariaSort={ariaSortFor("emp")}
                onSort={() => toggle("emp")}
                TH={TH}
              />
              <SortableHeader
                label="Workload / Periods"
                align="left"
                active={isActive("wl")}
                indicator={indicator("wl")}
                ariaSort={ariaSortFor("wl")}
                onSort={() => toggle("wl")}
                TH={TH}
              />
              {canSeeCost && (
                <SortableHeader
                  label="Cost/h"
                  align="right"
                  active={isActive("cost")}
                  indicator={indicator("cost")}
                  ariaSort={ariaSortFor("cost")}
                  onSort={() => toggle("cost")}
                  TH={TH}
                />
              )}
              <th style={{ ...TH, textAlign: "right" }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr
                key={p.id}
                style={{
                  transition: "background .1s",
                  verticalAlign: "top",
                  background: i % 2 ? C.surfaceAlt : C.surface,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.blueTint1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = i % 2 ? C.surfaceAlt : C.surface;
                }}
              >
                <td style={{ ...TD, textAlign: "left" }}>
                  <button
                    type="button"
                    onClick={() => openPerson(p.id)}
                    title="Open in Person view"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <Av name={p.name} sz={26} />
                    <span
                      style={{
                        fontWeight: 600,
                        color: C.navyDeep,
                        fontSize: 13,
                        textDecoration: "underline",
                        textDecorationColor: "transparent",
                        textUnderlineOffset: 2,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecorationColor = C.blue;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecorationColor = "transparent";
                      }}
                    >
                      {p.name}
                    </span>
                  </button>
                </td>
                <td style={{ ...TD, textAlign: "left", fontSize: 12, color: C.slateMut }}>
                  {p.email}
                </td>
                <td style={{ ...TD, textAlign: "center" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <SBadge s={p.status} />
                    {p.excludeFromPlanning && <Badge tone="slate">Excluded</Badge>}
                  </div>
                </td>
                <td style={{ ...TD, textAlign: "left" }}>
                  <TagEditor
                    value={p.roles}
                    suggestions={suggestions}
                    readOnly={!isAdmin}
                    onChange={(next) => onUpdateRoles(p.id, next)}
                  />
                </td>
                <td style={{ ...TD, textAlign: "center" }}>
                  {p.sysRole === null ? (
                    <span style={{ fontSize: 11, color: C.slateMut, fontStyle: "italic" }}>
                      Planning only
                    </span>
                  ) : (
                    <Badge tone={SYSROLE_TONE[p.sysRole] ?? "blue"}>
                      {SYSROLE_LABEL[p.sysRole] ?? p.sysRole}
                    </Badge>
                  )}
                </td>
                <td style={{ ...TD, textAlign: "center", fontSize: 12 }}>
                  {p.emp === "full" ? "Full-time" : "Variable"}
                </td>
                <td style={{ ...TD, textAlign: "left", fontSize: 12 }}>
                  {p.emp === "full" ? (
                    <span style={{ fontWeight: 600, color: C.g }}>100%</span>
                  ) : p.periods.length ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {p.periods.map((pr, pi) => (
                        <div
                          key={`${pr.from}-${pi}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: pr.wl >= 100 ? C.g : pr.wl >= 60 ? C.blue : C.y,
                              minWidth: 32,
                            }}
                          >
                            {pr.wl || 60}%
                          </span>
                          <span style={{ color: C.slateMut, fontSize: 11 }}>
                            {pr.from.slice(5)} → {pr.to.slice(5)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: C.slateMut }}>—</span>
                  )}
                </td>
                {canSeeCost && (
                  <PersonCostCell chain={costs[p.id] ?? []} projectsById={projectsById} TD={TD} />
                )}
                <td style={{ ...TD, textAlign: "right" }}>
                  {isAdmin ? (
                    <Btn small onClick={() => onEdit(p)}>
                      Edit
                    </Btn>
                  ) : (
                    <span style={{ fontSize: 10.5, color: C.slateMut }}>view only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <Empty
            title={people.length === 0 ? "No people yet" : "No people match"}
            msg={
              people.length === 0
                ? "Click + Add Person to create your first record."
                : "Try a different search query."
            }
          />
        )}
      </Card>
    </div>
  );
};
